import { formatName, formatDate as format } from '../lib/format';
import LoadingPopup from './LoadingPopup';
import { toast } from 'react-toastify';
import { runPdf } from '../utils/pdfSettle';
import React from "react";
import pdfMake from "pdfmake/build/pdfmake";
import { addDays } from 'date-fns';
import pdfFonts from "pdfmake/build/vfs_fonts";
import { useEffect } from "react";
import { resolvePdfGraphic } from "./pdfMedia";
import { uploadFileToStorage } from "../storageUpload";
pdfMake.vfs = pdfFonts['Roboto-Regular.ttf'];


const InvoiceGenerator = ({ invoice, logo, shouldUploadAndSendInvoice, updateInvoice, setChosenInvoice, setUploading, isWorker, onDownloadComplete, previewOnly = false, onPreviewReady, onPreviewError }) => {
    const formatPdfDate = (value) => format(new Date(value), 'dd MMM yyyy')
    const formatLineTime = (value) => String(value || '').replace(
        /(\d{2})\/([A-Za-z]{3})\/(\d{4})/g,
        (_, day, month, year) => `${day} ${month} ${year}`
    )

    useEffect(() => {
        if (invoice) {
            generatePDF().catch(error => {
                const action = previewOnly ? 'preview' : shouldUploadAndSendInvoice ? 'send' : 'download'
                toast.error(error?.message || `Unable to ${action} the invoice.`)
                if (setUploading) setUploading(false)
                if (!previewOnly && setChosenInvoice) setChosenInvoice('')
                // Otherwise the preview keeps saying "Generating invoice preview..." forever.
                if (previewOnly && onPreviewError) onPreviewError(error)
            })
        }
    }, [invoice])

    const generatePDF = async () => {

        const logoGraphic = await resolvePdfGraphic(logo);

        const safeNumber = (value) => {
            const num = Number(value);
            return Number.isFinite(num) ? num : 0;
        };
        const displayQuantity = (value) => {
            if (typeof value === 'number' && Number.isFinite(value)) {
                return String(value);
            }
            const raw = String(value || '').trim();
            if (!raw) return '0';
            return raw;
        };
        const formatCurrency = (value) => `$${safeNumber(value).toFixed(2)}`;
        const getQuantityType = (job) => (
            job.quantityType || (/^Equipment Hire\b/i.test(String(job.description || '').trim()) ? 'Days' : 'Hours')
        );
        const formatQuantity = (job) => {
            const quantity = displayQuantity(job.quantity);
            const quantityType = Number(job.quantity) === 1 && getQuantityType(job) === 'Days' ? 'Day' : getQuantityType(job);
            return `${quantity} ${quantityType}`;
        };

        const manualLineItems = Array.isArray(invoice.manualLineItems) ? invoice.manualLineItems : []
        const additionalJobs = Array.isArray(invoice.additionalJobs) ? invoice.additionalJobs : []
        const jobRows = [...(invoice.jobs || []), ...manualLineItems, ...additionalJobs].map((job) => [
            { text: `${job.description}${job.time ? ` \n ${formatLineTime(job.time)}` : ''}`, alignment: "left" },
            { text: job.textOnly ? '' : formatQuantity(job), alignment: "center" },
            { text: job.textOnly ? '' : formatCurrency(job.rate), alignment: "right" },
            { text: job.textOnly ? '' : formatCurrency(job.gst), alignment: "right" },
            { text: job.textOnly ? '' : formatCurrency(job.total), alignment: "right" },
        ]);
        const discountAmount = Math.max(0, safeNumber(invoice.discountAmount))
        const discountGst = Math.max(0, safeNumber(invoice.discountGst))
        if (discountAmount > 0) {
            jobRows.push([
                { text: invoice.discountDescription || 'Discount', alignment: 'left' },
                { text: '1 Unit', alignment: 'center' },
                { text: `-${formatCurrency(discountAmount)}`, alignment: 'right' },
                { text: `-${formatCurrency(discountGst)}`, alignment: 'right' },
                { text: `-${formatCurrency(discountAmount + discountGst)}`, alignment: 'right' },
            ])
        }

        const dueDays = Number(invoice.dueDays || 7)
        const resolvedDueDate = invoice.dueDate ? new Date(invoice.dueDate) : addDays(new Date(invoice.date), dueDays);
        const validDueDate = Number.isNaN(resolvedDueDate.getTime()) ? addDays(new Date(invoice.date), dueDays) : resolvedDueDate;
        const supplier = isWorker ? { ...invoice.worker, name: formatName(invoice.worker?.name || [invoice.worker?.firstName, invoice.worker?.lastName].filter(Boolean).join(' ')) } : invoice.company
        const customer = isWorker ? invoice.company : invoice.client
        const supplierAbn = String(supplier?.abn || '').trim()
        const customerAbn = String(customer?.abn || '').trim()
        const siteLabel = String(invoice.site?.address || invoice.site?.name || '').trim()
        const detailLine = (label, value) => value ? { text: [{ text: `${label}: `, bold: true }, String(value)] } : null

        const docDefinition = {
            pageSize: 'A4',
            pageMargins: [54, 68, 54, 54],
            content: [
                {
                    columns: [
                        logoGraphic ? {
                            ...logoGraphic,
                            fit: [125, 48],
                            width: 180,
                        } : { text: supplier?.name || '', bold: true, fontSize: 13, width: 180 },
                        {
                            text: "TAX INVOICE",
                            style: "invoiceTitle",
                            alignment: "right",
                        },
                    ],
                    margin: [0, 0, 0, 24],
                },
                {
                    columns: [
                        {
                            width: '*',
                            stack: [
                                { text: supplier?.name || '', bold: true },
                                supplierAbn ? { text: `ABN: ${supplierAbn}` } : null,
                                supplier?.address ? { text: supplier.address } : null,
                                supplier?.phone ? { text: supplier.phone } : null,
                                supplier?.email ? { text: supplier.email } : null,
                            ].filter(Boolean),
                        },
                        {
                            width: '*',
                            alignment: 'right',
                            stack: [
                                { text: [{ text: 'Bill To: ', bold: true }, customer?.name || ''] },
                                customerAbn ? { text: `ABN: ${customerAbn}` } : null,
                                customer?.address ? { text: customer.address } : null,
                            ].filter(Boolean),
                        },
                    ],
                    margin: [0, 0, 0, 18],
                },
                {
                    columns: [
                        { width: '*', text: siteLabel ? [{ text: 'Site: ', bold: true }, siteLabel] : '' },
                        {
                            width: 180,
                            alignment: 'right',
                            stack: [
                                detailLine('Invoice', invoice.invoiceNumber),
                                detailLine('Issued', formatPdfDate(invoice.date)),
                                detailLine('Due', formatPdfDate(validDueDate)),
                            ].filter(Boolean),
                        },
                    ],
                    margin: [0, 0, 0, 18],
                },
                {
                    table: {
                        headerRows: 1,
                        widths: ["*", 68, 62, 62, 72],
                        body: [
                            [
                                { text: "Description", style: "tableHeader" },
                                { text: "Quantity", style: "tableHeader", alignment: 'center' },
                                { text: "Rate", style: "tableHeader", alignment: 'right' },
                                { text: "GST", style: "tableHeader", alignment: 'right' },
                                { text: "Total", style: "tableHeader", alignment: 'right' },
                            ],
                            ...jobRows,
                        ],
                    },
                    layout: {
                        hLineWidth: () => 0.7,
                        vLineWidth: () => 0,
                        hLineColor: () => '#222222',
                        paddingLeft: () => 5,
                        paddingRight: () => 5,
                        paddingTop: () => 4,
                        paddingBottom: () => 4,
                    },
                },
                {
                    columns: [
                        { width: '*', text: '' },
                        {
                            width: 190,
                            table: {
                                widths: ['*', 76],
                                body: [
                                    [
                                        { text: "Subtotal", alignment: "right" },
                                        { text: formatCurrency(invoice.subtotal), alignment: "right" },
                                    ],
                                    [
                                        { text: "GST", alignment: "right" },
                                        { text: formatCurrency(invoice.gst), alignment: "right" },
                                    ],
                                    [
                                        { text: "Total", bold: true, alignment: "right" },
                                        { text: formatCurrency(invoice.total), bold: true, alignment: "right" },
                                    ],
                                ],
                            },
                            layout: {
                                hLineWidth: row => row === 3 ? 0.7 : 0,
                                vLineWidth: () => 0,
                                hLineColor: () => '#222222',
                                paddingTop: () => 3,
                                paddingBottom: () => 3,
                            },
                        },
                    ],
                    margin: [0, 0, 0, 48],
                },
                {
                    stack: [
                        { text: 'Payment Details', bold: true, margin: [0, 0, 0, 4] },
                        { text: `Account Name: ${supplier?.accountName || ''}` },
                        { text: `BSB: ${supplier?.bsb || ''}` },
                        { text: `Account Number: ${supplier?.accountNumber || ''}` },
                        { text: 'Reference: Use invoice number' },
                    ],
                },
            ],
            styles: {
                invoiceTitle: {
                    fontSize: 18,
                    bold: true,
                },
                tableHeader: {
                    bold: true,
                    fontSize: 10,
                },
            },
            defaultStyle: {
                fontSize: 10,
            },
        };

        const downloadBlob = (blob, fileName) => {
            const objectUrl = URL.createObjectURL(blob)
            const link = document.createElement('a')
            link.href = objectUrl
            link.download = fileName
            document.body.appendChild(link)
            link.click()
            link.remove()
            window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000)
        }

        async function uploadPdfToFirebase(blob, storageLocation, confidential = false) {
            if (setUploading) setUploading(true);
            let uploaded;
            try {
                uploaded = await uploadFileToStorage({
                    file: blob,
                    path: `${storageLocation}.pdf`,
                    mimeType: 'application/pdf',
                    confidential,
                    fallbackName: `invoice_${invoice.invoiceNumber}.pdf`,
                    orgId: invoice?.orgId || '',
                    ownerWorkerId: isWorker ? (invoice?.worker?.id || invoice?.workerId || '') : '',
                });
            } catch (err) {
                console.error("PDF Upload failed:", err);
                // The review dialog has already closed, so say the invoice did not go out.
                toast.error('The invoice was not sent: its PDF could not be uploaded. Please try again.');
                if (setUploading) setUploading(false);
                return false;
            }

            // updateInvoice reports its own errors; only surface a throw it did not handle,
            // and never mislabel a send failure as an upload failure.
            try {
                const updateSucceeded = await updateInvoice(uploaded.url);
                return updateSucceeded !== false;
            } catch (err) {
                toast.error(err?.message || 'The invoice could not be sent.');
                return false;
            } finally {
                if (setUploading) setUploading(false);
            }
        }


        if (previewOnly) {
            const blob = await runPdf(done => pdfMake.createPdf(docDefinition).getBlob(done))
            if (onPreviewReady) onPreviewReady(blob)
        } else if (shouldUploadAndSendInvoice) {
            // Not awaited: chosenInvoice is cleared straight away below, as before, and
            // the send carries on in the background.
            runPdf(done => pdfMake.createPdf(docDefinition).getBlob(done))
                .then(blob => uploadPdfToFirebase(blob, `Invoices/invoice_${invoice.invoiceNumber}`))
                .catch(error => toast.error(error?.message || "Unable to send invoice."))
        } else {
            // A failure here rejects generatePDF, and the catch above clears the popup.
            const blob = await runPdf(done => pdfMake.createPdf(docDefinition).getBlob(done))
            try {
                downloadBlob(blob, `invoice_${invoice.invoiceNumber}.pdf`)
                if (onDownloadComplete) await onDownloadComplete()
            } catch (error) {
                toast.error(error?.message || "Unable to download invoice attachments.")
            } finally {
                if (setChosenInvoice) setChosenInvoice('')
            }
        }

        if (!previewOnly && shouldUploadAndSendInvoice && setChosenInvoice) setChosenInvoice('')

    }

    return <LoadingPopup visible={!!invoice && !previewOnly && !shouldUploadAndSendInvoice} message="Downloading invoice..." />;
};

export default InvoiceGenerator;
